import { useIsAuthenticated } from "@azure/msal-react";
import { Stack } from "@mui/material";
import { EnvironmentHelper, readEnv } from "@repo/helpers";
import { type PageProps } from "gatsby";
import { type ComponentType, type FC, type PropsWithChildren, type ReactNode } from "react";

import { AppBarWrapper, cs } from "@repo/ui";
import { AppBar as AppBar404 } from "../../pages/404";
import { AppBar as ContactsAppBar, ContactsImage } from "../../pages/contacts";
import { AppBar as ContractsAppBar } from "../../pages/contracts";
import { AppBar as DocumentsAppBar } from "../../pages/documents";
import { AppBar as DPSAppBar } from "../../pages/doplnkove-penzijni-sporeni";
import { AppBar as UnsupportedAppBar } from "../../pages/nepodporovany-prohlizec";
import { AppBar as PPAppBar } from "../../pages/penzijni-pripojisteni";
import { AppBar as PrihlaseniAppBar } from "../../pages/prihlaseni";
import { AppBar as PrihlaseniNeboRegistraceAppBar } from "../../pages/prihlaseni-nebo-registrace";
import { AppBar as RegistraceAppBar } from "../../pages/registrace";
import { AppBar as CrossroadAppBar } from "../../pages/rozcestnik";
import { AppBar as UknownUserAppBar } from "../../pages/uzivatel-nenalezen";
import { LogoutOnlyAppBar, MaintenanceBar } from "../AppBar/Bars";
import BottomNavigation from "../BottomNavigation";
import { DataConfig } from "../DataConfig";
import Drawer from "../Drawer";
import { LogoutWarning } from "../Logout";
import { AppBar as ProfilesAppBar } from "../Profile/Layout";
import { CommonLifeAppBar } from "../ZivotniPojisteni/AppBar";
import LayoutContext, { LayoutProvider, useLayout, type BottomNavigationState } from "./context";
import { LayoutBody } from "./LayoutBody";

const appBarBySubPage: Record<string, ComponentType> = {
    ProfilesAppBar,
    DocumentsAppBar,
    ContractsAppBar,
    DPSAppBar,
    CommonLifeAppBar,
    PPAppBar,
    PrihlaseniNeboRegistraceAppBar,
    RegistraceAppBar,
    PrihlaseniAppBar,
    UknownUserAppBar,
    CrossroadAppBar,
    ContactsAppBar,
    AppBar404,
    MaintenanceBar,
    UnsupportedAppBar,
    LogoutOnlyAppBar,
};

export { LayoutContext, LayoutProvider };

export const TitleByPage = {
    contacts: cs.bottomNavigationBar.contacts,
    profile: cs.bottomNavigationBar.profile,
};

export const BannerWrapperDesktop = ({ children }: PropsWithChildren) => (
    <Stack sx={{ gap: 4, alignItems: "stretch", width: "100%", mb: 5 }}>{children}</Stack>
);

export const BannerWrapperMobile = ({ children }: PropsWithChildren) => (
    <Stack sx={{ gap: 4, mb: 4 }}>{children}</Stack>
);

export const BackgroundImageByPage = {
    contacts: ContactsImage,
};

export type LayoutProps = {
    appBarContent?: ReactNode;
    withGlobalDrawer?: boolean;
    bottomNavigation: boolean;
    appBar: string;
} & PageProps;

const isDev = readEnv("NODE_ENV") == "development";

const getBottomNavigation = (pageProp: boolean, navState: BottomNavigationState): boolean => {
    if (navState === "unset") {
        return pageProp;
    }
    return navState === "shown";
};

const Layout: FC<LayoutProps> = (layoutProps) => {
    const { children, withGlobalDrawer = true } = layoutProps;
    const { bottomNavigationState } = useLayout();
    const showBottomNavigation = getBottomNavigation(layoutProps.bottomNavigation, bottomNavigationState);

    const isAuthenticated = useIsAuthenticated();
    const AppBarContent = appBarBySubPage[layoutProps.appBar] ?? appBarBySubPage.AppBar404;
    return (
        <>
            <Stack
                sx={{
                    ...(showBottomNavigation && { pb: { xs: 8, md: 0 } }),
                }}
            >
                {layoutProps.appBar !== "none" && (
                    <AppBarWrapper>
                        <AppBarContent {...layoutProps} />
                    </AppBarWrapper>
                )}
                <LayoutBody
                    pageProps={{
                        name: layoutProps.pageContext.page,
                        ...layoutProps.pageContext.additionalProps,
                    }}
                >
                    {children}
                </LayoutBody>
                {showBottomNavigation && <BottomNavigation />}
            </Stack>
            {!EnvironmentHelper.isProd() && <DataConfig />}
            {withGlobalDrawer && <Drawer />}
            {isAuthenticated && !isDev && (
                <LogoutWarning pageName={layoutProps.pageContext.page as string | undefined} />
            )}
        </>
    );
};

export default Layout;
